import type { TaskView } from '@/lib/api/generated/model';
import { isTerminalStatus, latestExecution } from './executionStatus';

export interface DeletionImpact {
  /** Direct subtasks that go with the task. */
  count: number;
  /** Whether deleting will interrupt work in flight. */
  anyRunning: boolean;
}

/**
 * Whether the task has work in flight that a delete would interrupt.
 *
 * A run waiting on its subtasks (`awaiting_children`) is not terminal, but it
 * is not executing either: there is nothing to stop, and counting it would put
 * the warning on every waiting parent. Its running subtasks are checked on
 * their own.
 */
function isWorking(task: TaskView): boolean {
  const latest = latestExecution(task.executions ?? []);
  // Checked first: the service raises `chain_in_progress` for any non-terminal
  // run, a waiting join included, so the flag alone cannot tell them apart.
  if (latest?.status === 'awaiting_children') return false;
  if (task.chain_in_progress) return true;
  return latest !== undefined && !isTerminalStatus(latest.status);
}

/**
 * What deleting `task` takes with it, for the confirmation to say plainly.
 *
 * The count is the larger of what the board holds and what the parent's own
 * summary reports, so a child the board has not loaded still counts. Deeper
 * descendants are deleted too but are not counted (spec 137 Assumptions).
 */
export function deletionImpact(
  task: TaskView,
  workspaceTasks: readonly TaskView[],
): DeletionImpact {
  const children = workspaceTasks.filter(
    (candidate) => candidate.parent_task_id === task.id,
  );
  const summary = task.linked_tasks;
  return {
    count: Math.max(children.length, summary?.total ?? 0),
    anyRunning:
      isWorking(task) ||
      children.some(isWorking) ||
      (summary?.tasks ?? []).some((child) => child.status === 'in_progress'),
  };
}
